import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePeriod, type Period } from '../lib/period';
import { Card, CardHeader, EmptyState, PageHeader, Table, Td, Th, Tr } from '../components/ui';
import { KpiCard, PeriodLine, useNumber } from '../components/kit';
import { AXIS, ChartFrame, GRID, SERIES, TableTwin, dotStyle, legendText, tooltipItemStyle, tooltipLabelStyle, tooltipStyle } from '../components/b-charts';
import { CodeBadge, FilterRow, Loaded, Note, ParamSelect, PersonLink, useAmount, useCode, useDates, useFilterOptions } from '../components/b-shared';

/**
 * Points & Rewards (CR-008 §7): the wallet's own ledger, read and never written — kept apart from work
 * performance. A grant's reason and who made it are an administrator's private notes and never reach this
 * page; a day reward is shown in days, never as the pay it is worth.
 */

interface PointsSummary {
  period: Period;
  totals: { granted: number; redeemed: number; refunded: number; net: number; bySource: { AI_DAILY_POINTS: number; ADMINISTRATOR: number; OTHER: number } };
  balances: { total: number; wallets: number; withPoints: number };
  trend: Array<{ key: string; granted: number; redeemed: number; refunded: number }>;
  redemptions: Array<{ type: string; status: string; count: number; points: number }>;
  cash: Array<{ currency: string; requested: number; paid: number; cancelled: number; pending: number }>;
  dayRewards: Array<{ status: string; days: number; count: number }>;
  employees: Array<{ user: { id: string; fullName: string; fullNameAr: string | null; jobTitle: string | null }; granted: number; redeemed: number; refunded: number; balance: number }>;
  states: { redemption: string[]; cash: string[]; dayReward: string[] };
}

const SOURCES = ['AI_DAILY_POINTS', 'ADMINISTRATOR', 'OTHER'] as const;

export function PointsPage() {
  const { t, localized } = useI18n();
  const { params, param } = usePeriod();
  const filters = useFilterOptions();
  const departmentId = param('departmentId');
  const userId = param('userId');

  const summary = useQuery({
    queryKey: ['executive', 'points', 'summary', params, departmentId, userId],
    queryFn: () => api.get<PointsSummary>('/executive/points/summary', { ...params, departmentId, userId }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title={t('points.title')} subtitle={t('points.subtitle')} />
      <FilterRow>
        <ParamSelect
          param="departmentId"
          label={t('common.department')}
          allLabel={t('employees.allDepartments')}
          options={(filters.data?.departments ?? []).map((d) => ({ value: d.id, label: localized(d) }))}
        />
        <ParamSelect
          param="userId"
          label={t('common.employee')}
          allLabel={t('points.allEmployees')}
          options={(filters.data?.employees ?? []).map((e) => ({ value: e.id, label: e.isActive ? localized(e) : `${localized(e)} (${t('common.formerEmployee')})` }))}
        />
      </FilterRow>
      {userId && departmentId && <Note className="-mt-3 mb-4">{t('points.employeeWins')}</Note>}
      <Loaded query={summary}>{(data) => <PointsBody data={data} />}</Loaded>
    </>
  );
}

function PointsBody({ data }: { data: PointsSummary }) {
  const { t } = useI18n();
  const { query } = usePeriod();
  const num = useNumber();
  const code = useCode();
  const amount = useAmount();
  const tot = data.totals;
  const sourceTotal = SOURCES.reduce((s, k) => s + (tot.bySource[k] ?? 0), 0);

  // Redemptions: one row a type, a column a status.
  const statuses = data.states.redemption.length ? data.states.redemption : [...new Set(data.redemptions.map((r) => r.status))];
  const types = [...new Set(data.redemptions.map((r) => r.type))];
  const cell = (type: string, status: string) => data.redemptions.find((r) => r.type === type && r.status === status);

  // Day rewards: the days and the rewards of each status.
  const rewardStatuses = data.states.dayReward.length ? data.states.dayReward : [...new Set(data.dayRewards.map((d) => d.status))];
  const rewardRows = rewardStatuses.map((status) => {
    const mine = data.dayRewards.filter((d) => d.status === status);
    return { status, rewards: mine.reduce((s, d) => s + d.count, 0), days: mine.reduce((s, d) => s + d.days * d.count, 0), parts: mine };
  });

  return (
    <div className="space-y-5">
      <PeriodLine period={data.period} />
      <Card>
        <div className="space-y-1.5">
          <Note>{t('points.separate')}</Note>
          <Note>{t('points.private')}</Note>
          <Note>{t('points.daysOnly')}</Note>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label={t('points.totals.granted')} value={num(tot.granted)} hint={t('points.totals.grantedHint')} />
        <KpiCard label={t('points.totals.redeemed')} value={num(tot.redeemed)} hint={t('points.totals.redeemedHint')} />
        <KpiCard label={t('points.totals.refunded')} value={num(tot.refunded)} hint={t('points.totals.refundedHint')} />
        <KpiCard label={t('points.totals.net')} value={<span dir="ltr">{`${tot.net > 0 ? '+' : tot.net < 0 ? '−' : ''}${num(Math.abs(tot.net))}`}</span>} hint={t('points.totals.netHint')} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('points.bySource.title')} subtitle={t('points.bySource.subtitle')} />
          <dl className="space-y-2">
            {SOURCES.map((k) => (
              <div key={k} className="flex items-baseline justify-between gap-4 rounded-md bg-[var(--surface-sunken)] px-3 py-2.5">
                <dt className="text-body-sm text-[var(--text-secondary)]">{t(`points.source.${k}`)}</dt>
                <dd className="text-title tabular text-[var(--text-primary)]">
                  {num(tot.bySource[k] ?? 0)}
                  {sourceTotal > 0 && <span className="ms-2 text-caption text-muted">{num(((tot.bySource[k] ?? 0) / sourceTotal) * 100, 0)}%</span>}
                </dd>
              </div>
            ))}
          </dl>
        </Card>
        <Card>
          <CardHeader title={t('points.balances.title')} subtitle={t('points.balances.subtitle')} />
          <dl className="space-y-2">
            <div className="flex items-baseline justify-between gap-4 rounded-md bg-[var(--surface-sunken)] px-3 py-2.5">
              <dt className="text-body-sm text-[var(--text-secondary)]">{t('points.balances.total')}</dt>
              <dd className="text-title tabular">{num(data.balances.total)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 rounded-md bg-[var(--surface-sunken)] px-3 py-2.5">
              <dt className="text-body-sm text-[var(--text-secondary)]">{t('points.balances.wallets')}</dt>
              <dd className="text-title tabular">{num(data.balances.wallets)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 rounded-md bg-[var(--surface-sunken)] px-3 py-2.5">
              <dt className="text-body-sm text-[var(--text-secondary)]">{t('points.balances.withPoints')}</dt>
              <dd className="text-title tabular">{num(data.balances.withPoints)}</dd>
            </div>
          </dl>
        </Card>
      </div>

      <PointsTrend data={data} />

      <div className="grid gap-5 xl:grid-cols-2">
        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader title={t('points.redemptions.byType')} subtitle={t('points.redemptions.byTypeHint')} />
          </div>
          {types.length === 0 ? (
            <EmptyState title={t('points.redemptions.empty')} hint={t('common.noResultsHint')} />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('points.redemptions.type')}</Th>
                  {statuses.map((s) => (
                    <Th key={s} align="end">
                      {code('points.redemptionStatus', s)}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {types.map((type) => (
                  <Tr key={type}>
                    <Td className="min-w-[11rem]">{code('points.redemption', type)}</Td>
                    {statuses.map((s) => {
                      const c = cell(type, s);
                      return (
                        <Td key={s} align="end" className="tabular">
                          {c ? (
                            <span className="flex flex-col items-end">
                              <span>{num(c.count)}</span>
                              <span className="text-caption text-muted">{t('points.pointsValue', { n: num(c.points) })}</span>
                            </span>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader title={t('points.cash.title')} subtitle={t('points.cash.subtitle')} />
          </div>
          {data.cash.length === 0 ? (
            <EmptyState title={t('points.cash.empty')} hint={t('common.noResultsHint')} />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.currency')}</Th>
                  <Th align="end">{t('points.cash.requested')}</Th>
                  <Th align="end">{t('points.cash.paid')}</Th>
                  <Th align="end">{t('points.cash.cancelled')}</Th>
                  <Th align="end">{t('points.cash.pending')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.cash.map((c) => (
                  <Tr key={c.currency}>
                    <Td>
                      <span dir="ltr" className="font-mono text-caption">
                        {c.currency}
                      </span>
                    </Td>
                    <Td align="end" className="tabular">
                      {amount(c.requested, c.currency)}
                    </Td>
                    <Td align="end" className="tabular">
                      {amount(c.paid, c.currency)}
                    </Td>
                    <Td align="end" className="tabular">
                      {amount(c.cancelled, c.currency)}
                    </Td>
                    <Td align="end" className="tabular">
                      {amount(c.pending, c.currency)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
          <div className="px-5 py-3">
            <Note>{t('points.cash.note')}</Note>
          </div>
        </Card>
      </div>

      <Card padded={false}>
        <div className="px-5 pt-5">
          <CardHeader title={t('points.dayRewards.title')} subtitle={t('points.dayRewards.summarySubtitle')} />
        </div>
        {data.dayRewards.length === 0 ? (
          <EmptyState title={t('points.dayRewards.empty')} hint={t('common.noResultsHint')} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.status')}</Th>
                <Th align="end">{t('points.dayRewards.rewards')}</Th>
                <Th align="end">{t('points.dayRewards.totalDays')}</Th>
                <Th>{t('points.dayRewards.parts')}</Th>
              </tr>
            </thead>
            <tbody>
              {rewardRows.map((r) => (
                <Tr key={r.status}>
                  <Td>
                    <CodeBadge prefix="points.dayRewardStatus" value={r.status} />
                  </Td>
                  <Td align="end" className="tabular">
                    {num(r.rewards)}
                  </Td>
                  <Td align="end" className="tabular">
                    {num(r.days, 2)}
                  </Td>
                  <Td className="text-caption text-[var(--text-secondary)]">
                    {r.parts.length
                      ? r.parts
                          .sort((a, b) => a.days - b.days)
                          .map((p) => t('points.dayRewards.part', { days: num(p.days, 2), n: num(p.count) }))
                          .join(' · ')
                      : '—'}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card padded={false}>
        <div className="px-5 pt-5">
          <CardHeader title={t('points.employees.title')} subtitle={t('points.employees.subtitle')} />
        </div>
        {data.employees.length === 0 ? (
          <EmptyState title={t('points.employees.empty')} hint={t('common.noResultsHint')} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.employee')}</Th>
                <Th align="end">{t('points.totals.granted')}</Th>
                <Th align="end">{t('points.totals.redeemed')}</Th>
                <Th align="end">{t('points.totals.refunded')}</Th>
                <Th align="end">{t('points.employees.balance')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.employees.map((e) => (
                <Tr key={e.user.id}>
                  <Td className="min-w-[12rem]">
                    <PersonLink person={e.user} query={query} tab="points" />
                    {e.user.jobTitle && <span className="block text-caption text-muted">{e.user.jobTitle}</span>}
                  </Td>
                  <Td align="end" className="tabular">
                    {num(e.granted)}
                  </Td>
                  <Td align="end" className="tabular">
                    {num(e.redeemed)}
                  </Td>
                  <Td align="end" className="tabular">
                    {num(e.refunded)}
                  </Td>
                  <Td align="end" className="tabular">
                    {num(e.balance)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

const addDay = (day: string) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};
const addMonth = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 7);
};

/** Every day (or month) of the period so far, a quiet one as the zero it was. */
function fillTrend(trend: PointsSummary['trend'], period: Period) {
  const monthly = period.days > 62 || trend.some((p) => p.key.length === 7);
  const keys = new Set<string>();
  if (monthly) {
    for (let m = period.start.slice(0, 7); m <= period.elapsed.last.slice(0, 7); m = addMonth(m)) keys.add(m);
  } else {
    for (let d = period.start; d <= period.elapsed.last; d = addDay(d)) keys.add(d);
  }
  for (const p of trend) keys.add(p.key);
  const byKey = new Map(trend.map((p) => [p.key, p]));
  return { monthly, rows: [...keys].sort().map((key) => byKey.get(key) ?? { key, granted: 0, redeemed: 0, refunded: 0 }) };
}

function PointsTrend({ data }: { data: PointsSummary }) {
  const { t } = useI18n();
  const num = useNumber();
  const dates = useDates();
  const { monthly, rows } = fillTrend(data.trend, data.period);
  const chartRows = rows.map((r) => ({ ...r, label: monthly ? dates.shortMonth(r.key) : dates.shortDay(r.key) }));
  const names = { granted: t('points.totals.granted'), redeemed: t('points.totals.redeemed'), refunded: t('points.totals.refunded') };
  const summary = t('points.trend.summary', { granted: num(data.totals.granted), redeemed: num(data.totals.redeemed), refunded: num(data.totals.refunded) });
  return (
    <Card>
      <CardHeader title={t('points.trend.title')} subtitle={monthly ? t('points.trend.byMonth') : t('points.trend.byDay')} />
      {data.trend.length === 0 ? (
        <EmptyState title={t('points.trend.empty')} hint={t('common.noResultsHint')} />
      ) : (
        <>
          <ChartFrame label={summary}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" {...AXIS} tickLine={false} minTickGap={18} />
                <YAxis {...AXIS} width={48} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} formatter={(value) => num(Number(value))} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(value: string) => legendText(value)} />
                <Line type="linear" dataKey="granted" name={names.granted} stroke={SERIES[0]} strokeWidth={2} dot={rows.length <= 31 ? dotStyle(SERIES[0]) : false} isAnimationActive={false} />
                <Line type="linear" dataKey="redeemed" name={names.redeemed} stroke={SERIES[1]} strokeWidth={2} dot={rows.length <= 31 ? dotStyle(SERIES[1]) : false} isAnimationActive={false} />
                <Line type="linear" dataKey="refunded" name={names.refunded} stroke={SERIES[2]} strokeWidth={2} dot={rows.length <= 31 ? dotStyle(SERIES[2]) : false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartFrame>
          <TableTwin summary={t('points.trend.table')}>
            <Table>
              <thead>
                <tr>
                  <Th>{monthly ? t('employee.trend.month') : t('common.date')}</Th>
                  <Th align="end">{names.granted}</Th>
                  <Th align="end">{names.redeemed}</Th>
                  <Th align="end">{names.refunded}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Tr key={r.key}>
                    <Td>{monthly ? dates.month(r.key) : dates.day(r.key)}</Td>
                    <Td align="end" className="tabular">
                      {num(r.granted)}
                    </Td>
                    <Td align="end" className="tabular">
                      {num(r.redeemed)}
                    </Td>
                    <Td align="end" className="tabular">
                      {num(r.refunded)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableTwin>
        </>
      )}
    </Card>
  );
}
